import { create } from "zustand";

const STORAGE_KEY = "ld_time_tracking";
const MODE_STORAGE_KEY = "ld_mode_time";

export type StudyModeKey = "cards" | "learn" | "test" | "games" | "philology";

export interface ModeSeconds {
  cards: number;
  learn: number;
  test: number;
  games: number;
  philology: number;
}

export interface PersistedTimeData {
  date: string;
  siteSeconds: number;
  studySeconds: number;
  lastSyncedSiteMinutes: number;
  lastSyncedStudyMinutes: number;
}

function loadFromStorage(): PersistedTimeData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const today = new Date().toISOString().slice(0, 10);
    if (parsed?.date !== today) return null;
    return {
      date: parsed.date,
      siteSeconds: Number(parsed.siteSeconds) || 0,
      studySeconds: Number(parsed.studySeconds) || 0,
      lastSyncedSiteMinutes: Number(parsed.lastSyncedSiteMinutes) || 0,
      lastSyncedStudyMinutes: Number(parsed.lastSyncedStudyMinutes) || 0,
    };
  } catch {
    return null;
  }
}

function saveToStorage(data: PersistedTimeData) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

function loadModeSeconds(): ModeSeconds {
  try {
    const raw = localStorage.getItem(MODE_STORAGE_KEY);
    if (!raw) return { cards: 0, learn: 0, test: 0, games: 0, philology: 0 };
    const parsed = JSON.parse(raw);
    const today = new Date().toISOString().slice(0, 10);
    if (parsed?.date !== today) return { cards: 0, learn: 0, test: 0, games: 0, philology: 0 };
    return {
      cards: Number(parsed.cards) || 0,
      learn: Number(parsed.learn) || 0,
      test: Number(parsed.test) || 0,
      games: Number(parsed.games) || 0,
      philology: Number(parsed.philology) || 0,
    };
  } catch {
    return { cards: 0, learn: 0, test: 0, games: 0, philology: 0 };
  }
}

function saveModeSeconds(ms: ModeSeconds) {
  try {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem(MODE_STORAGE_KEY, JSON.stringify({ date: today, ...ms }));
  } catch {}
}

/** Persisted time tracking. Survives reload/navigation. */
export interface TimeTrackingState {
  siteSeconds: number;
  studySeconds: number;
  isInStudyView: boolean;
  lastSyncedSiteMinutes: number;
  lastSyncedStudyMinutes: number;
  /**
   * Per-mode seconds for the current day.
   * Stored in localStorage only; not synced to DB. Resets on reload/device change.
   * Dashboard Time Distribution merges this with user_card_events for live accuracy.
   */
  modeSeconds: ModeSeconds;
  setInStudyView: (value: boolean) => void;
  addSiteSeconds: (seconds: number) => void;
  addStudySeconds: (seconds: number) => void;
  /** Add seconds for a specific study mode */
  addModeSeconds: (mode: StudyModeKey, seconds: number) => void;
  /** Call after syncing to DB. Updates lastSynced so we only send delta next time. */
  markSynced: (siteMinutes: number, studyMinutes: number) => void;
  /** Restore from localStorage. Returns { siteMinutes, studyMinutes } delta to sync to DB. */
  restoreFromStorage: () => { siteMinutes: number; studyMinutes: number } | null;
}

export const useTimeTrackingStore = create<TimeTrackingState>((set, get) => ({
  siteSeconds: 0,
  studySeconds: 0,
  isInStudyView: false,
  lastSyncedSiteMinutes: 0,
  lastSyncedStudyMinutes: 0,
  modeSeconds: loadModeSeconds(),

  setInStudyView: (value) => set({ isInStudyView: value }),

  addSiteSeconds: (seconds) => {
    set((s) => {
      const siteSeconds = s.siteSeconds + seconds;
      const today = new Date().toISOString().slice(0, 10);
      saveToStorage({
        date: today,
        siteSeconds,
        studySeconds: s.studySeconds,
        lastSyncedSiteMinutes: s.lastSyncedSiteMinutes,
        lastSyncedStudyMinutes: s.lastSyncedStudyMinutes,
      });
      return { siteSeconds };
    });
  },

  addStudySeconds: (seconds) => {
    set((s) => {
      const studySeconds = s.studySeconds + seconds;
      const today = new Date().toISOString().slice(0, 10);
      saveToStorage({
        date: today,
        siteSeconds: s.siteSeconds,
        studySeconds,
        lastSyncedSiteMinutes: s.lastSyncedSiteMinutes,
        lastSyncedStudyMinutes: s.lastSyncedStudyMinutes,
      });
      return { studySeconds };
    });
  },

  addModeSeconds: (mode, seconds) => {
    set((s) => {
      const modeSeconds = { ...s.modeSeconds, [mode]: s.modeSeconds[mode] + seconds };
      saveModeSeconds(modeSeconds);
      return { modeSeconds };
    });
  },

  markSynced: (siteMinutes, studyMinutes) => {
    set((s) => {
      const today = new Date().toISOString().slice(0, 10);
      saveToStorage({
        date: today,
        siteSeconds: s.siteSeconds,
        studySeconds: s.studySeconds,
        lastSyncedSiteMinutes: siteMinutes,
        lastSyncedStudyMinutes: studyMinutes,
      });
      return {
        lastSyncedSiteMinutes: siteMinutes,
        lastSyncedStudyMinutes: studyMinutes,
      };
    });
  },

  restoreFromStorage: () => {
    const stored = loadFromStorage();
    if (!stored) return null;
    const siteMinutes = Math.floor(stored.siteSeconds / 60);
    const studyMinutes = Math.floor(stored.studySeconds / 60);
    const deltaSite = Math.max(0, siteMinutes - stored.lastSyncedSiteMinutes);
    const deltaStudy = Math.max(0, studyMinutes - stored.lastSyncedStudyMinutes);
    const restoredMode = loadModeSeconds();
    if (deltaSite === 0 && deltaStudy === 0) {
      set({
        siteSeconds: stored.siteSeconds,
        studySeconds: stored.studySeconds,
        lastSyncedSiteMinutes: stored.lastSyncedSiteMinutes,
        lastSyncedStudyMinutes: stored.lastSyncedStudyMinutes,
        modeSeconds: restoredMode,
      });
      return null;
    }
    set({
      siteSeconds: stored.siteSeconds,
      studySeconds: stored.studySeconds,
      lastSyncedSiteMinutes: stored.lastSyncedSiteMinutes,
      lastSyncedStudyMinutes: stored.lastSyncedStudyMinutes,
      modeSeconds: restoredMode,
    });
    return { siteMinutes: deltaSite, studyMinutes: deltaStudy };
  },
}));
